import {
  ChangeEvent,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import './rich-text-editor.css'

interface RichTextEditorProps {
  value: string
  onChange: (value: string) => void
  onImageUpload?: (file: File) => Promise<string>
  placeholder?: string
  readOnly?: boolean
}

export interface RichTextEditorHandle {
  insertImage: (url: string) => void
}

const TOOLBAR_WITH_IMAGE = [
  [{ header: [1, 2, 3, false] }],
  ['bold', 'italic', 'underline'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['link', 'image'],
  ['clean'],
]

const TOOLBAR_WITHOUT_IMAGE = [
  [{ header: [1, 2, 3, false] }],
  ['bold', 'italic', 'underline'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['link'],
  ['clean'],
]

export const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(
  ({ value, onChange, onImageUpload, placeholder, readOnly = false }, ref) => {
    const quillRef = useRef<ReactQuill>(null)
    const onChangeRef = useRef(onChange)
    onChangeRef.current = onChange

    const insertImage = useCallback((url: string) => {
      const quill = quillRef.current?.getEditor()
      if (!quill) return
      const range = quill.getSelection() || { index: quill.getLength() - 1 }
      quill.insertEmbed(range.index, 'image', url)
      quill.setSelection(range.index + 1, 0)
    }, [])

    useImperativeHandle(ref, () => ({ insertImage }), [insertImage])

    // A persistent (DOM-present) hidden input, so the toolbar opens it and tests can attach a file.
    const fileInputRef = useRef<HTMLInputElement>(null)

    const imageHandler = useCallback(() => {
      fileInputRef.current?.click()
    }, [])

    const handleFileSelected = async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      e.target.value = '' // allow re-selecting the same file
      if (!file || !onImageUpload) return

      try {
        const url = await onImageUpload(file)
        insertImage(url)
      } catch (err) {
        console.error('Image upload failed:', err)
      }
    }

    useEffect(() => {
      const quill = quillRef.current?.getEditor()
      if (!quill || !onImageUpload) return

      const inFlight = new WeakSet<HTMLImageElement>()
      const handleTextChange = async (
        _delta: unknown,
        _oldDelta: unknown,
        source: string
      ): Promise<void> => {
        if (source !== 'user') return
        const imgs = Array.from(
          quill.root.querySelectorAll('img[src^="data:"]')
        ) as HTMLImageElement[]
        const toUpload = imgs.filter((img) => !inFlight.has(img))
        if (toUpload.length === 0) return
        toUpload.forEach((img) => inFlight.add(img))

        await Promise.all(
          toUpload.map(async (img) => {
            try {
              const blob = await (await fetch(img.src)).blob()
              const ext = (blob.type.split('/')[1] || 'png').split('+')[0]
              const file = new File([blob], `pasted-${Date.now()}.${ext}`, {
                type: blob.type || 'image/png',
              })
              const url = await onImageUpload(file)
              img.setAttribute('src', url)
            } catch (e) {
              console.error('Failed to upload pasted image', e)
              img.remove()
            }
          })
        )
        onChangeRef.current(quill.root.innerHTML)
      }

      quill.on('text-change', handleTextChange)
      return () => {
        quill.off('text-change', handleTextChange)
      }
    }, [onImageUpload])

    const modules = useMemo(
      () => ({
        toolbar: {
          container: onImageUpload ? TOOLBAR_WITH_IMAGE : TOOLBAR_WITHOUT_IMAGE,
          handlers: onImageUpload ? { image: imageHandler } : {},
        },
      }),
      [onImageUpload, imageHandler]
    )

    const formats = useMemo(
      () => ['header', 'bold', 'italic', 'underline', 'list', 'bullet', 'link', 'image'],
      []
    )

    return (
      <div className="rich-text-editor">
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={onChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          readOnly={readOnly}
        />
        {onImageUpload && (
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            data-testid="rich-text-image-input"
            style={{ display: 'none' }}
            onChange={handleFileSelected}
          />
        )}
      </div>
    )
  }
)
